<template>
  <div class="mt-3" data-testid="sheet-action-bar">
    <!-- RN-11: motivo do bloqueio à vista no rodapé da tabela, além do tooltip do botão. -->
    <p
      v-if="showRowControls && reason"
      class="text-sm text-neutral50 mb-2"
      data-testid="sheet-advance-block-reason"
    >
      {{ reason }}
    </p>

    <div class="flex flex-wrap items-center gap-3">
      <template v-if="showRowControls">
        <!-- RF-12/RN-11: "Adicionar linha" some ao final da tabela; desabilitado no teto,
          com o motivo em tooltip — mesmo padrão de motivo à vista já usado no menu de
          coluna (RF-06). -->
        <v-tooltip location="top" :text="addRowReason ?? ''" :disabled="!addRowReason">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <OutlineButton
                text="Adicionar linha"
                width="150px"
                height="36px"
                data-testid="sheet-add-row-button"
                :disabled="!canAddRow"
                @click="emit('addRow')"
              />
            </span>
          </template>
        </v-tooltip>

        <!--
          Simétrico de "Adicionar linha": sem ele a única via de exclusão é o menu de
          clique-direito da lib, que ninguém descobre — ainda mais com a coluna de índice
          de linha escondida (`hideIndex`). Desabilitado no piso de linhas, com o motivo em
          tooltip, no mesmo padrão do botão acima.
        -->
        <v-tooltip location="top" :text="deleteRowReason ?? ''" :disabled="!deleteRowReason">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <OutlineButton
                text="Excluir última linha"
                width="185px"
                height="36px"
                data-testid="sheet-delete-row-button"
                :disabled="!canDeleteRow"
                @click="emit('deleteRow')"
              />
            </span>
          </template>
        </v-tooltip>

        <!-- RF-07 (#2115): caminho sem atalho para o Ctrl+Z/Ctrl+Y nativos do jspreadsheet. -->
        <OutlineButton
          text="Desfazer"
          width="120px"
          height="36px"
          data-testid="sheet-undo-button"
          :disabled="!canUndo"
          @click="emit('undo')"
        />

        <!--
          RF-10/RN-11 (#2118) — popover "Atalhos": documenta a CAPACIDADE da grade
          (o conjunto completo de atalhos), distinto e sem subordinação ao tooltip acima
          (que documenta ESTADO — por que "Adicionar linha" está bloqueado agora).
        -->
        <div ref="shortcutsContainerRef" class="relative">
          <OutlineButton
            text="Atalhos"
            width="120px"
            height="36px"
            data-testid="sheet-shortcuts-button"
            aria-haspopup="true"
            :aria-expanded="showShortcuts"
            aria-controls="sheet-shortcuts-popover"
            @click="toggleShortcuts"
          />
          <!--
            RNF Acessibilidade — sem `role="menu"`: ao contrário do menu de coluna
            (`sheet-column-menu-popover`), cujos itens são AÇÕES (`role="menuitem"`), este
            popover só documenta atalhos — linhas de texto estático, sem interação própria.
            `menu` exige filhos `menuitem`/`menuitemcheckbox`/`menuitemradio`; forçar esse
            papel em `<p>`s não-acionáveis seria um role inválido só para imitar o outro
            popover. Um grupamento simples com rótulo é o que a semântica real pede aqui.
          -->
          <div
            v-if="showShortcuts"
            id="sheet-shortcuts-popover"
            role="group"
            aria-label="Lista de atalhos de teclado"
            class="sheet-shortcuts-popover"
            data-testid="sheet-shortcuts-popover"
          >
            <p
              v-for="shortcut in SHEET_SHORTCUTS"
              :key="shortcut.keys"
              class="sheet-shortcuts-popover-item"
            >
              <strong>{{ shortcut.keys }}</strong> — {{ shortcut.description }}
            </p>
          </div>
        </div>
      </template>

      <v-autocomplete
        :model-value="selectedSheetModel"
        :items="sheetModels"
        item-title="label"
        item-value="id"
        label="Modelo de Folha"
        variant="outlined"
        density="compact"
        class="min-w-[220px]"
        hide-details
        return-object
        data-testid="sheet-template-select"
        @update:model-value="(model) => emit('selectSheetModel', model as SheetModelOption)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * RF-12/#2119 — barra de ações da folha de monitoração (engine `spreadsheet`, flag
 * ligada). Consolida os três elementos soltos que já existiam em
 * `DsAttendanceAwaitingAudit.vue` (motivo do bloqueio, "Desfazer", autocomplete de
 * modelo) e adiciona "Adicionar linha" (RF-12) — nenhum elemento de UI novo na
 * fronteira do teto (RN-11): tooltip e rodapé reusam o mesmo padrão do motivo do menu
 * de coluna (RF-06). O componente é agnóstico de domínio: quem decide `reason`,
 * `canAddRow` etc. é a tela chamadora.
 */
import { ref, watch, onBeforeUnmount } from 'vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { SHEET_SHORTCUTS } from '@/components/designSystem/Cards/sheet-keyboard'
import type { SheetModelOption } from '../utils/sheet-action-bar.types'

const props = defineProps<{
  /** `isSheetV2` — engine `native` (flag desligada) não tem nenhum destes controles. */
  showRowControls: boolean
  /** RN-11 — motivo do bloqueio de avanço, exibido no rodapé só quando presente. */
  reason: string | null
  canUndo: boolean
  /** RN-09/RF-12 — falso no teto de linhas. */
  canAddRow: boolean
  /** RN-11 — motivo do bloqueio de "Adicionar linha", exibido em tooltip quando presente. */
  addRowReason?: string
  /** RN-09 — falso no piso de linhas (a folha nunca fica sem nenhuma). */
  canDeleteRow: boolean
  /** RN-11 — motivo do bloqueio de "Excluir última linha", exibido em tooltip quando presente. */
  deleteRowReason?: string
  sheetModels: SheetModelOption[]
  selectedSheetModel: SheetModelOption | null
}>()

const emit = defineEmits<{
  undo: []
  addRow: []
  deleteRow: []
  selectSheetModel: [model: SheetModelOption]
}>()

/**
 * RF-10/RN-11 (#2118) — popover "Atalhos": documenta a lista completa de `sheet-keyboard.ts`
 * (design system — este componente é `Modules`, pode importar de lá; a direção proibida é
 * a contrária). Fecha ao clicar fora, mesmo padrão do menu de coluna do
 * `AttendanceSheetCard`.
 */
const showShortcuts = ref(false)
const shortcutsContainerRef = ref<HTMLElement | null>(null)

function toggleShortcuts() {
  showShortcuts.value = !showShortcuts.value
}

function handleOutsideClick(event: MouseEvent) {
  if (shortcutsContainerRef.value?.contains(event.target as Node)) return
  showShortcuts.value = false
}

/** RNF Acessibilidade — Esc fecha o popover pelo teclado, sem depender de um mousedown fora. */
function handleEscKey(event: KeyboardEvent) {
  if (event.key === 'Escape') showShortcuts.value = false
}

watch(showShortcuts, (open) => {
  if (open) {
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscKey)
  } else {
    document.removeEventListener('mousedown', handleOutsideClick)
    document.removeEventListener('keydown', handleEscKey)
  }
})

watch(
  () => props.showRowControls,
  (showRowControls) => {
    if (!showRowControls && showShortcuts.value) {
      showShortcuts.value = false
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleOutsideClick)
  document.removeEventListener('keydown', handleEscKey)
})
</script>

<style scoped>
/*
 * RF-10/RF-11 — sem referência visual no protótipo (decidido depois dele, ver corpo da
 * issue #2118) — segue os tokens do me-bricks já usados no popover de menu de coluna do
 * `AttendanceSheetCard` (`sheet-column-menu-popover`), sem inventar token novo.
 */
.sheet-shortcuts-popover {
  position: absolute;
  z-index: 60;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 280px;
  max-width: 360px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  padding: 8px 0;
  text-align: left;
}

.sheet-shortcuts-popover-item {
  margin: 0;
  padding: 4px 12px;
  font-size: 0.8125rem;
  color: #16161d;
}
</style>
